import { FC, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import classNames from 'classnames'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Pagination, Navigation, Autoplay } from 'swiper/modules'
import { Swiper as SwiperType } from 'swiper'

import styles from './Sponsors.module.scss'
import SponsorCard from './SponsorCard'

import { sponsorsData } from 'resources'
import { SectionLayout } from 'components'
import { useLockBodyScroll, useOutsideClick, useResponsive, useVersion } from 'hooks'
import { ReactComponent as CloseIcon } from 'assets/svg/close.svg'
import { ReactComponent as ArrowIcon } from 'assets/svg/arrow.svg'

type SponsorsProps = {
  comingSoon?: boolean
  addToRefs?: (el: HTMLDivElement) => void
}

const Sponsors: FC<SponsorsProps> = ({ addToRefs, comingSoon = false }) => {
  const modalRef = useRef<any>(null)
  const swiperRef = useRef()

  const { version } = useVersion()
  const { isLaptop } = useResponsive()

  const data = sponsorsData[version]

  const [isOpen, setIsOpen] = useState(false)
  const [currentIndex, setCurrentIndex] = useState<number>(0)
  const [activeSponsorId, setActiveSponsorId] = useState<string | null>(null)

  const updateIndex = (swiperInstance: SwiperType) => {
    if (swiperInstance === null) return
    const currentSlide = swiperInstance?.activeIndex
    setCurrentIndex(currentSlide)
  }

  const modalToggler = (id?: string) => {
    setIsOpen(!isOpen)
    setActiveSponsorId(id ?? null)
    setCurrentIndex(0)
  }

  useLockBodyScroll(isOpen)
  useOutsideClick(modalRef, () => {
    setIsOpen(false)
    setCurrentIndex(0)
  })

  const activeSponsorInfo = data.flatMap(data => data.sponsors).find(sponsor => sponsor.id === activeSponsorId)
  const renderSponsorsList = data.map(data => <SponsorCard key={data.id} data={data} modalToggler={modalToggler} />)
  const isSlider = Number(activeSponsorInfo?.vacantions?.length) > 1

  const renderVacantionsList = activeSponsorInfo?.vacantions?.map(vacantion => (
    <div className={styles.modal__vacantions_item}>
      <img src={activeSponsorInfo.image} alt='sponsor' />
      <p className={styles.modal__vacantions_item_text}>{vacantion.title}</p>
      <a
        href={vacantion.url}
        target='_blank'
        rel='noopener noreferrer'
        className={classNames(styles.modal__vacantions_link, {
          [styles.modal__vacantions_link_adobe]: activeSponsorInfo.name === 'adobe',
          [styles.modal__vacantions_link_grid]: activeSponsorInfo.name === 'griddynamics',
          [styles.modal__vacantions_link_aua]: activeSponsorInfo.name === 'aua',
        })}
      >
        Apply
      </a>
    </div>
  ))

  const renderSliderVacantionsList = activeSponsorInfo?.vacantions?.map(vacantion => (
    <SwiperSlide key={vacantion.id}>
      <div className={styles.modal__vacantions_item}>
        <img src={activeSponsorInfo.image} alt='sponsor' />
        <p className={styles.modal__vacantions_item_text}>{vacantion.title}</p>
        <a
          href={vacantion.url}
          target='_blank'
          rel='noopener noreferrer'
          className={classNames(styles.modal__vacantions_link, {
            [styles.modal__vacantions_link_adobe]: activeSponsorInfo.name === 'adobe',
            [styles.modal__vacantions_link_grid]: activeSponsorInfo.name === 'griddynamics',
            [styles.modal__vacantions_link_aua]: activeSponsorInfo.name === 'aua',
          })}
        >
          Apply
        </a>
      </div>
    </SwiperSlide>
  ))

  const pagination = {
    clickable: true,
    renderBullet: function (_: number, className: string) {
      return `<span class="${className}"> </span>`
    },
  }

  return (
    <SectionLayout id='SPONSORS' addToRefs={addToRefs}>
      <div className='container'>
        <p className={styles.wrapper__title}>Sponsors and Partners</p>
        {comingSoon ? (
          <div className={styles.soon} />
        ) : (
          <div className={styles.wrapper__list__gaper}>{renderSponsorsList}</div>
        )}
      </div>

      {createPortal(
        <>
          {isOpen && (
            <div className={styles.modal}>
              <div
                ref={modalRef}
                className={classNames(styles.modal__content, {
                  [styles.modal__content_single]: activeSponsorInfo?.vacantions?.length === 1,
                })}
              >
                <CloseIcon className={styles.modal__close} onClick={modalToggler as any} />
                <div
                  className={classNames(styles.modal__vacantions, 'vacantionsSlider', {
                    vacantionsSliderSingle: !isSlider,
                  })}
                >
                  {!isLaptop ? (
                    renderVacantionsList
                  ) : (
                    <Swiper
                      loop={isLaptop}
                      initialSlide={0}
                      onRealIndexChange={updateIndex}
                      onBeforeInit={(swiper: any) => {
                        swiperRef.current = swiper as any
                      }}
                      pagination={pagination}
                      modules={[Autoplay, Pagination, Navigation]}
                      spaceBetween={28}
                      slidesPerView={1}
                    >
                      {renderSliderVacantionsList}
                      {isSlider && (
                        <>
                          <button
                            disabled={!currentIndex}
                            className='swaper--prev'
                            onClick={() => {
                              if (currentIndex < 1) {
                                ;(swiperRef.current as any)?.slideTo(0)
                              } else {
                                ;(swiperRef.current as any)?.slideTo(currentIndex - 1)
                              }
                            }}
                          >
                            <ArrowIcon />
                          </button>
                          <button
                            disabled={currentIndex === Number(activeSponsorInfo?.vacantions?.length) - 1}
                            className='swaper--next'
                            onClick={() => (swiperRef.current as any)?.slideTo(currentIndex + 1)}
                          >
                            <ArrowIcon />
                          </button>
                        </>
                      )}
                    </Swiper>
                  )}
                </div>
              </div>
            </div>
          )}
        </>,
        document.body
      )}
    </SectionLayout>
  )
}

export default Sponsors
